import type { APIMessage } from '../contracts/chat-types.js';
import { MessageType } from '../contracts/chat-types.js';
import { t as defaultT, type Translate } from '../i18n/index.js';

const BOOSTS = new Set<number>([
  MessageType.GUILD_BOOST,
  MessageType.GUILD_BOOST_TIER_1,
  MessageType.GUILD_BOOST_TIER_2,
  MessageType.GUILD_BOOST_TIER_3,
]);

/**
 * Texto das mensagens do sistema (entrou, fixou, thread criada, adicionou ao
 * grupo…), como o Discord escreve. Nulo: não é mensagem do sistema (a
 * interface desenha normal). `name` dá o nome de exibição de quem fez.
 */
export function systemMessageText(
  message: APIMessage,
  name: (userId: string) => string,
  t: Translate = defaultT,
): string | null {
  const autor = name(message.author.id);
  const alvo = message.mentions[0] ? name(message.mentions[0].id) : '';
  switch (message.type) {
    case MessageType.RECIPIENT_ADD:
      return t('sistema.adicionou', { autor, alvo });
    case MessageType.RECIPIENT_REMOVE:
      return message.mentions[0]?.id === message.author.id
        ? t('sistema.saiu', { autor })
        : t('sistema.removeu', { autor, alvo });
    case MessageType.CALL:
      return t('sistema.chamada', { autor });
    case MessageType.CHANNEL_NAME_CHANGE:
      return t('sistema.renomeou', { autor, nome: message.content });
    case MessageType.CHANNEL_ICON_CHANGE:
      return t('sistema.iconeGrupo', { autor });
    case MessageType.CHANNEL_PINNED_MESSAGE:
      return t('sistema.fixou', { autor });
    case MessageType.USER_JOIN: {
      // Como o Discord: a frase sai do ID (a mesma para todo mundo).
      const variants = t('sistema.entrou').split('|');
      const index = Number(BigInt(message.id) % BigInt(variants.length));
      return variants[index].replace('{autor}', autor);
    }
    case MessageType.CHANNEL_FOLLOW_ADD:
      return t('sistema.seguiu', { autor, nome: message.content });
    case MessageType.THREAD_CREATED:
      return t('sistema.threadCriada', { autor, nome: message.content });
    case MessageType.THREAD_STARTER_MESSAGE:
      return t('sistema.inicioThread');
    case MessageType.AUTO_MODERATION_ACTION:
      return t(
        message.embeds[0]?.fields?.some(
          (field) => field.name === 'flagged_message_id',
        )
          ? 'sistema.moderacaoAlertou'
          : 'sistema.moderacaoBloqueou',
        { autor },
      );
    case MessageType.POLL_RESULT: {
      const pergunta =
        message.embeds[0]?.fields?.find(
          (field) => field.name === 'poll_question_text',
        )?.value ?? '';
      return t('sistema.enqueteEncerrada', { autor, pergunta });
    }
    default:
      if (BOOSTS.has(message.type)) return t('sistema.impulsionou', { autor });
      return null;
  }
}
